import { useEffect, useState } from 'react';
import { X, Sparkles } from 'lucide-react';

/**
 * Phase 27.50.B.18 — Storefront Welcome Banner.
 *
 * Friendly one-liner introducing the new Marketing Center storefront
 * after the Print Orders retirement. Shows a maximum of THREE times
 * per browser (tracked via localStorage counter) and hides forever
 * when the user taps the dismiss button.
 */
const STORAGE_KEY = 'kr:shop_welcome_views';
const MAX_VIEWS = 3;

export function StorefrontWelcomeBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let raw = 0;
    try { raw = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10) || 0; }
    catch { raw = 0; }
    if (raw >= MAX_VIEWS) return;
    setVisible(true);
    // Increment the view counter once per mount so refresh + tab
    // switches still get credited against the 3-time budget.
    try { localStorage.setItem(STORAGE_KEY, String(raw + 1)); }
    catch { /* localStorage blocked — banner still shows */ }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, String(MAX_VIEWS)); }
    catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="storefront-welcome-banner"
      className="mb-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-teal-50 to-sky-50 p-4 flex items-start gap-3 shadow-sm"
    >
      <div className="w-9 h-9 rounded-full bg-white/70 border border-emerald-200 flex items-center justify-center shrink-0">
        <Sparkles size={16} className="text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-emerald-900">
          Welcome to the new Marketing Storefront
        </p>
        <p className="text-xs text-emerald-800/80 mt-1">
          Browse, add to cart and check out marketing pieces in a single flow.
          Prices, bulk tiers and shipping are handled right here — the old
          Print Orders queue has been retired.
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss welcome banner"
        className="shrink-0 text-emerald-700/70 hover:text-emerald-900 hover:bg-white/60 rounded-md p-1"
        data-testid="storefront-welcome-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
